import { useState, useEffect } from 'react';
import { useSEO, generateMusicSchoolSchema, generateWebPageSchema, generateFAQSchema } from '@/utils/seo';

export default function Home() {
  const [charCount, setCharCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  // SEO設定
  useSEO({
    title: '国分寺市バイオリン教室 矢谷和子バイオリン教室 | 東京都',
    description: '東京都国分寺市で65年の伝統を誇る矢谷和子バイオリン教室。アットホームで明るい雰囲気の中、初心者から音楽大学を目指す方まで丁寧に指導。子供コース、大人コース、音大受験コース。無料体験レッスン受付中。国分寺駅徒歩10分。駐車場あり。',
    keywords: 'バイオリン教室,国分寺市,東京都,矢谷和子,音楽教室',
    canonical: `${siteUrl}/`,
    ogTitle: '国分寺市バイオリン教室 矢谷和子バイオリン教室 | 東京都',
    ogDescription: '東京都国分寺市で65年の伝統を誇る矢谷和子バイオリン教室。アットホームで明るい雰囲気の中、初心者から音楽大学を目指す方まで丁寧に指導。無料体験レッスン受付中。',
    ogType: 'website',
    ogUrl: `${siteUrl}/`
  });

  useEffect(() => {
    // Schema.org JSON-LDを追加
    const schemas = [
      generateMusicSchoolSchema(),
      generateWebPageSchema(),
      generateFAQSchema()
    ];

    schemas.forEach((schema, index) => {
      const scriptId = `schema-${index}`;
      let scriptTag = document.getElementById(scriptId);
      
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.id = scriptId;
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }
      
      scriptTag.textContent = JSON.stringify(schema);
    });

    return () => {
      schemas.forEach((_, index) => {
        const scriptTag = document.getElementById(`schema-${index}`);
        if (scriptTag) {
          scriptTag.remove();
        }
      });
    };
  }, []);

  useEffect(() => {
    const textarea = document.getElementById('message') as HTMLTextAreaElement;
    if (textarea) {
      textarea.addEventListener('input', (e) => {
        const target = e.target as HTMLTextAreaElement;
        setCharCount(target.value.length);
      });
    }

    // アニメーション用のvisibility設定
    setIsVisible(true);

    // スクロールアニメーション
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.scroll-animate');
    animatedElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validate
    const message = formData.get('message') as string;
    if (message.length > 500) {
      const messageDiv = document.getElementById('form-message');
      if (messageDiv) {
        messageDiv.innerHTML = '<p class="text-red-600">メッセージは500文字以内で入力してください。</p>';
      }
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('https://readdy.ai/api/form/d4cjhi2ahcjh6clrr9bg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      const messageDiv = document.getElementById('form-message');
      if (response.ok) {
        if (messageDiv) {
          messageDiv.innerHTML = '<p class="text-green-600 font-semibold">お問い合わせありがとうございます。確認後、ご連絡させていただきます。</p>';
        }
        form.reset();
        setCharCount(0);
      } else {
        if (messageDiv) {
          messageDiv.innerHTML = '<p class="text-red-600">送信に失敗しました。もう一度お試しください。</p>';
        }
      }
    } catch (error) {
      const messageDiv = document.getElementById('form-message');
      if (messageDiv) {
        messageDiv.innerHTML = '<p class="text-red-600">送信に失敗しました。もう一度お試しください。</p>';
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        .animate-fade-in {
          animation: fadeIn 0.8s ease-out forwards;
        }

        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }

        .animate-slide-in-left {
          animation: slideInLeft 0.8s ease-out forwards;
        }

        .animate-slide-in-right {
          animation: slideInRight 0.8s ease-out forwards;
        }

        .animate-pulse-slow {
          animation: pulse 2s ease-in-out infinite;
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }

        .scroll-animate {
          opacity: 0;
          transform: translateY(30px);
        }

        .hover-scale {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .hover-scale:hover {
          transform: translateY(-5px) scale(1.02);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
        }

        .gradient-text {
          background: linear-gradient(45deg, #f59e0b, #d97706);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

      {/* Header */}
      <header className={`bg-white shadow-sm transition-all duration-500 ${isVisible ? 'animate-fade-in' : 'opacity-0'}`}>
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="relative w-12 h-12 flex items-center justify-center animate-float">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500 to-amber-700 rounded-full opacity-10"></div>
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="24" cy="24" r="22" fill="url(#gradient)" stroke="#D97706" strokeWidth="1.5"/>
                  <path d="M18 14L30 34M28 16L32 24M16 24L20 32" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="29" cy="17" r="2" fill="#FFFFFF"/>
                  <circle cx="19" cy="31" r="2" fill="#FFFFFF"/>
                  <path d="M26 20C26 20 28 22 28 24C28 26 26 28 26 28" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round"/>
                  <defs>
                    <linearGradient id="gradient" x1="4" y1="4" x2="44" y2="44" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#F59E0B"/>
                      <stop offset="1" stopColor="#D97706"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>矢谷和子バイオリン教室</h1>
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="#about" className="text-gray-700 hover:text-amber-600 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer">教室について</a>
              <a href="#courses" className="text-gray-700 hover:text-amber-600 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer">レッスン内容</a>
              <a href="#instructor" className="text-gray-700 hover:text-amber-600 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer">講師紹介</a>
              <a href="#access" className="text-gray-700 hover:text-amber-600 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer">アクセス</a>
              <a href="#contact" className="text-gray-700 hover:text-amber-600 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer">お問い合わせ</a>
            </div>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-transform duration-300 hover:scale-110"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-gray-700 transition-transform duration-300 ${mobileMenuOpen ? 'rotate-180' : ''}`}></i>
            </button>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3 animate-fade-in-up">
              <a href="#about" onClick={() => setMobileMenuOpen(false)} className="block text-gray-700 hover:text-amber-600 transition-all duration-300 hover:translate-x-2 cursor-pointer">教室について</a>
              <a href="#courses" onClick={() => setMobileMenuOpen(false)} className="block text-gray-700 hover:text-amber-600 transition-all duration-300 hover:translate-x-2 cursor-pointer">レッスン内容</a>
              <a href="#instructor" onClick={() => setMobileMenuOpen(false)} className="block text-gray-700 hover:text-amber-600 transition-all duration-300 hover:translate-x-2 cursor-pointer">講師紹介</a>
              <a href="#access" onClick={() => setMobileMenuOpen(false)} className="block text-gray-700 hover:text-amber-600 transition-all duration-300 hover:translate-x-2 cursor-pointer">アクセス</a>
              <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="block text-gray-700 hover:text-amber-600 transition-all duration-300 hover:translate-x-2 cursor-pointer">お問い合わせ</a>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 hover:scale-105"
          style={{
            backgroundImage: `linear-gradient(rgba(255, 251, 235, 0.85), rgba(255, 251, 235, 0.85)), url('https://public.readdy.ai/ai/img_res/a66b895a1aa03fe7c1a961792c36aeac.jpg')`
          }}
        ></div>
        <div className={`relative z-10 max-w-2xl text-center transition-all duration-1000 ${isVisible ? 'animate-slide-in-left' : 'opacity-0 translate-x-[-50px]'}`}>
          <h2 className="text-5xl font-bold text-gray-800 mb-6 gradient-text" style={{ fontFamily: '"Noto Serif JP", serif' }}>
            音と人を育ててきた<br />バイオリン教室です
          </h2>
          <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              東京都国分寺市で65年の伝統を誇るバイオリン教室<br />
              初心者からプロを目指す方まで、丁寧に指導いたします
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#contact" className="bg-amber-600 text-white px-8 py-4 rounded-full hover:bg-amber-700 transition-all duration-300 hover:scale-105 hover:shadow-lg text-lg font-semibold shadow-lg whitespace-nowrap cursor-pointer animate-pulse-slow">
                無料体験レッスンを申し込む
              </a>
              <a href="#courses" className="bg-white text-amber-600 px-8 py-4 rounded-full hover:bg-gray-50 transition-all duration-300 hover:scale-105 hover:shadow-lg text-lg font-semibold border-2 border-amber-600 whitespace-nowrap cursor-pointer">
                レッスン内容を見る
              </a>
            </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 scroll-animate">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Noto Serif JP', serif" }}>教室について</h3>
            <div className="w-20 h-1 bg-amber-600 mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="scroll-animate">
              <img 
                src="https://readdy.ai/api/search-image?query=A%20beautiful%20vintage%20violin%20resting%20elegantly%20on%20an%20antique%20wooden%20music%20stand%20with%20soft%20natural%20lighting%20surrounded%20by%20classical%20sheet%20music%20and%20a%20warm%20nostalgic%20atmosphere%20in%20Tokyo%20Japan.%20The%20scene%20captures%20sixty%20five%20years%20of%20musical%20heritage%20with%20elegant%20composition%20featuring%20rich%20wood%20tones%20and%20a%20timeless%20quality%20representing%20tradition%20and%20craftsmanship.%20Professional%20studio%20photography%20with%20warm%20color%20grading%20emphasizing%20the%20beauty%20and%20tradition%20of%20classical%20music%20education%20with%20simple%20clean%20background&width=600&height=400&seq=about001&orientation=landscape"
                alt="バイオリン教室の雰囲気"
                title="矢谷和子バイオリン教室 バイオリン 国分寺市"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover object-top hover-scale"
              />
            </div>
            <div className="scroll-animate">
              <h4 className="text-2xl font-bold text-gray-800 mb-6">65年の伝統と実績</h4>
              <p className="text-gray-700 mb-6 leading-relaxed text-lg">
                矢谷和子バイオリン教室は、1959年の開校以来、東京都国分寺市で65年にわたり、多くの生徒さんにバイオリンの楽しさと音楽の素晴らしさを伝えてまいりました。
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed text-lg">
                アットホームで明るい雰囲気の中、一人ひとりのペースに合わせた丁寧な指導を心がけています。初めてバイオリンに触れる小さなお子様から、趣味として楽しみたい大人の方、音楽大学を目指す方まで、幅広く対応しております。
              </p>
              <div className="grid grid-cols-3 gap-6 mt-8">
                <div className="text-center hover-scale">
                  <div className="text-4xl font-bold text-amber-600 mb-2 gradient-text">65年</div>
                  <div className="text-gray-600">の伝統</div>
                </div>
                <div className="text-center hover-scale">
                  <div className="text-4xl font-bold text-amber-600 mb-2 gradient-text">500名+</div>
                  <div className="text-gray-600">卒業生</div>
                </div>
                <div className="text-center hover-scale">
                  <div className="text-4xl font-bold text-amber-600 mb-2 gradient-text">個別</div>
                  <div className="text-gray-600">指導</div>
                </div>
              </div>
            </div>
          </div>

          {/* 発表会セクション */}
          <div className="mt-20 scroll-animate">
            <div className="text-center mb-12">
              <h4 className="text-3xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Noto Serif JP', serif" }}>年に一度の発表会</h4>
              <div className="w-16 h-1 bg-amber-600 mx-auto"></div>
            </div>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="scroll-animate">
                <h5 className="text-2xl font-bold text-gray-800 mb-6">春の発表会で成長を実感</h5>
                <p className="text-gray-700 mb-6 leading-relaxed text-lg">
                  毎年春に開催される発表会は、生徒さんたちが一年間の成果を披露する大切な舞台です。小さなお子様から大人の方まで、それぞれのレベルに応じた演奏を発表いたします。
                </p>
                <p className="text-gray-700 mb-6 leading-relaxed text-lg">
                  舞台での演奏体験は、技術の向上だけでなく、自信と表現力を育む貴重な機会となっています。ご家族やお友達に見守られながら、音楽の喜びを分かち合う素晴らしいひとときです。
                </p>
                <div className="space-y-4">
                  <div className="flex items-center text-gray-700 hover:translate-x-2 transition-transform duration-300">
                    <i className="ri-calendar-line text-amber-600 text-xl mr-3 animate-pulse-slow"></i>
                    <span className="font-semibold">開催時期：</span>
                    <span className="ml-2">毎年春（3月〜4月）</span>
                  </div>
                  <div className="flex items-center text-gray-700 hover:translate-x-2 transition-transform duration-300">
                    <i className="ri-map-pin-line text-amber-600 text-xl mr-3 animate-pulse-slow"></i>
                    <span className="font-semibold">会場：</span>
                    <span className="ml-2">国分寺市内ホール</span>
                  </div>
                  <div className="flex items-center text-gray-700 hover:translate-x-2 transition-transform duration-300">
                    <i className="ri-user-line text-amber-600 text-xl mr-3 animate-pulse-slow"></i>
                    <span className="font-semibold">参加者：</span>
                    <span className="ml-2">全コース生徒対象</span>
                  </div>
                </div>
              </div>
              <div className="scroll-animate">
                <img 
                  src="https://static.readdy.ai/image/600af45053aeb2d03fd9159422e2737a/89cd7b1c3dcc113a5ed6956e7949ccb0.jpeg"
                  alt="矢谷和子バイオリン教室 発表会の様子"
                  title="矢谷和子バイオリン教室 発表会 国分寺市"
                  className="rounded-2xl shadow-2xl w-full h-[400px] object-cover object-top hover-scale"
                />
                <p className="text-center text-gray-600 mt-4 text-sm">
                  発表会での演奏風景。生徒さんたちが一年間の成果を披露します。
                </p>
              </div>
            </div>
          </div>

          {/* アンサンブル アカデミア */}
          <div className="mt-20 scroll-animate">
            <div className="text-center mb-12">
              <h4 className="text-3xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Noto Serif JP', serif" }}>アンサンブル アカデミア</h4>
              <div className="w-16 h-1 bg-amber-600 mx-auto"></div>
            </div>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="scroll-animate">
                <img 
                  src="https://static.readdy.ai/image/600af45053aeb2d03fd9159422e2737a/897129a59bba79d4197008e27cc0028c.jpeg"
                  alt="アンサンブル アカデミア 弦楽合奏団"
                  title="アンサンブル アカデミア 弦楽合奏団 国分寺市"
                  className="rounded-2xl shadow-2xl w-full h-[400px] object-cover object-top hover-scale"
                />
              </div>
              <div className="scroll-animate">
                <h5 className="text-2xl font-bold text-gray-800 mb-6">弦楽合奏の楽しみを</h5>
                <p className="text-gray-700 mb-6 leading-relaxed text-lg">
                  アンサンブル アカデミアは、当教室の生徒さんたちが集まり活動する弦楽合奏団です。アンサンブルの楽しみを感じながら、技術の向上を目指して日々練習に励んでいます。
                </p>
                <p className="text-gray-700 mb-6 leading-relaxed text-lg">
                  個人レッスンでは味わえない、仲間と一緒に音楽を創り上げる喜びを体験できます。春の発表会や定期演奏会を通じて、アンサンブルの素晴らしさを多くの方々にお届けしています。
                </p>
                <div className="space-y-4">
                  <div className="flex items-center text-gray-700 hover:translate-x-2 transition-transform duration-300">
                    <i className="ri-music-2-line text-amber-600 text-xl mr-3 animate-pulse-slow"></i>
                    <span className="font-semibold">活動内容：</span>
                    <span className="ml-2">弦楽合奏の練習と演奏</span>
                  </div>
                  <div className="flex items-center text-gray-700 hover:translate-x-2 transition-transform duration-300">
                    <i className="ri-calendar-event-line text-amber-600 text-xl mr-3 animate-pulse-slow"></i>
                    <span className="font-semibold">演奏会：</span>
                    <span className="ml-2">春の発表会・定期演奏会</span>
                  </div>
                  <div className="flex items-center text-gray-700 hover:translate-x-2 transition-transform duration-300">
                    <i className="ri-team-line text-amber-600 text-xl mr-3 animate-pulse-slow"></i>
                    <span className="font-semibold">メンバー：</span>
                    <span className="ml-2">教室生徒で構成</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Courses Section */}
      <section id="courses" className="py-20 px-4 bg-white scroll-animate">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h4 className="text-4xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Noto Serif JP', serif" }}>レッスン内容</h4>
            <div className="w-20 h-1 bg-amber-600 mx-auto"></div>
          </div>

          {/* レッスン風景写真 */}
          <div className="mb-16 scroll-animate">
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <img 
                src="https://static.readdy.ai/image/600af45053aeb2d03fd9159422e2737a/2e695da5aa64819a2cb2744210d60ee5.jpeg"
                alt="矢谷和子バイオリン教室 レッスン風景"
                title="矢谷和子バイオリン教室 レッスン風景 国分寺市"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover object-top hover-scale"
                style={{ filter: 'saturate(1.3) contrast(1.1) brightness(1.05)' }}
              />
              <img 
                src="https://static.readdy.ai/image/600af45053aeb2d03fd9159422e2737a/77c774e2ca8a13dd92529d0e75a5e8ea.jpeg"
                alt="矢谷和子バイオリン教室 レッスン風景"
                title="矢谷和子バイオリン教室 レッスン風景 国分寺市"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover object-top hover-scale"
                style={{ filter: 'saturate(1.3) contrast(1.1) brightness(1.05)' }}
              />
            </div>
            <p className="text-center text-gray-600 mt-4 text-sm">
              丁寧な個別指導で、一人ひとりのペースに合わせたレッスンを行っています。
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-amber-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover-scale scroll-animate">
              <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mb-6 mx-auto animate-pulse-slow">
                <i className="ri-user-smile-line text-3xl text-white"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-800 mb-4 text-center">子供コース</h4>
              <p className="text-gray-700 mb-6 leading-relaxed">
                3歳から始められる子供向けコースです。楽しみながら基礎をしっかり学び、音楽の喜びを体験できます。
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                  <i className="ri-check-line text-amber-600 mt-1 mr-2"></i>
                  <span>年齢に応じた教材</span>
                </li>
                <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                  <i className="ri-check-line text-amber-600 mt-1 mr-2"></i>
                  <span>楽しく学べる指導法</span>
                </li>
                <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                  <i className="ri-check-line text-amber-600 mt-1 mr-2"></i>
                  <span>発表会で成長を実感</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover-scale scroll-animate">
              <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mb-6 mx-auto animate-pulse-slow">
                <i className="ri-user-heart-line text-3xl text-white"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-800 mb-4 text-center">大人コース</h4>
              <p className="text-gray-700 mb-6 leading-relaxed">
                初心者の方も安心して始められます。趣味として音楽を楽しみたい方に最適なコースです。
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                  <i className="ri-check-line text-amber-600 mt-1 mr-2"></i>
                  <span>初心者大歓迎</span>
                </li>
                <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                  <i className="ri-check-line text-amber-600 mt-1 mr-2"></i>
                  <span>好きな曲で練習</span>
                </li>
                <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                  <i className="ri-check-line text-amber-600 mt-1 mr-2"></i>
                  <span>柔軟なスケジュール</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover-scale scroll-animate">
              <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mb-6 mx-auto animate-pulse-slow">
                <i className="ri-trophy-line text-3xl text-white"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-800 mb-4 text-center">音大受験コース</h4>
              <p className="text-gray-700 mb-6 leading-relaxed">
                音楽大学を目指す方のための専門コース。確かな技術と表現力を身につけます。
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                  <i className="ri-check-line text-amber-600 mt-1 mr-2"></i>
                  <span>受験対策指導</span>
                </li>
                <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                  <i className="ri-check-line text-amber-600 mt-1 mr-2"></i>
                  <span>高度な技術習得</span>
                </li>
                <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                  <i className="ri-check-line text-amber-600 mt-1 mr-2"></i>
                  <span>合格実績多数</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-16 bg-amber-50 rounded-2xl p-8 scroll-animate hover-scale">
            <h4 className="text-2xl font-bold text-gray-800 mb-6 text-center">レッスン料金</h4>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-6 shadow hover-scale">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-lg font-semibold text-gray-800">月3回コース（30分）</span>
                  <span className="text-2xl font-bold text-amber-600 gradient-text">¥10,000～</span>
                </div>
                <p className="text-gray-600">初心者・子供向けの基本コース</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow hover-scale">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-lg font-semibold text-gray-800">月3回コース（45分～）</span>
                  <span className="text-2xl font-bold text-amber-600 gradient-text">¥15,000～</span>
                </div>
                <p className="text-gray-600">中級者・音大受験向けコース</p>
              </div>
            </div>
            <p className="text-center text-gray-600 mt-6">※入会金：¥5,000（初回のみ）</p>
            <p className="text-center text-gray-500 mt-2 text-sm">回数や時間は柔軟に対応いたします</p>
          </div>
        </div>
      </section>

      {/* Instructor Section */}
      <section id="instructor" className="py-20 px-4 scroll-animate">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h4 className="text-4xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Noto Serif JP', serif" }}>講師紹介</h4>
            <div className="w-20 h-1 bg-amber-600 mx-auto"></div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            {/* 矢谷和子先生 */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover-scale scroll-animate">
              <div className="p-8">
                <div className="text-center mb-6">
                  <div className="w-48 h-60 rounded-2xl overflow-hidden shadow-lg mx-auto mb-4 hover:scale-105 transition-transform duration-300">
                    <img 
                      src="https://static.readdy.ai/image/600af45053aeb2d03fd9159422e2737a/3e2f220ae72c5119bd1b96d4665b44f2.jpeg"
                      alt="矢谷和子講師"
                      title="矢谷和子 バイオリン講師 国分寺市"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <h4 className="text-2xl font-bold text-gray-800 mb-2">矢谷 和子</h4>
                  <p className="text-lg text-amber-600 mb-4 gradient-text">主宰・バイオリン・ビオラ指導</p>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed text-sm">
                  指導歴６５年。数多くの生徒を指導し、国内外コンクール受賞者や音大合格者を多数育ててまいりました。
                </p>
                <p className="text-gray-700 mb-6 leading-relaxed text-sm">
                  「音楽は心を豊かにする」をモットーに、一人ひとりの個性を大切にした指導を心がけています。初心者の方も安心してお越しください。
                </p>
                <div className="space-y-2">
                  <div className="flex items-center text-gray-700 text-sm hover:translate-x-1 transition-transform duration-300">
                    <i className="ri-music-line text-amber-600 mr-2 text-lg"></i>
                    <span>指導歴65年</span>
                  </div>
                  <div className="flex items-center text-gray-700 text-sm hover:translate-x-1 transition-transform duration-300">
                    <i className="ri-award-fill text-amber-600 mr-2 text-lg"></i>
                    <span>2025年、国分寺市より≪文化功労賞≫受賞</span>
                  </div>
                  <div className="flex items-center text-gray-700 text-sm hover:translate-x-1 transition-transform duration-300">
                    <i className="ri-user-star-line text-amber-600 mr-2 text-lg"></i>
                    <span>国内外コンクール受賞者・音大合格者 多数輩出</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 奈良幸子先生 */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover-scale scroll-animate">
              <div className="p-8">
                <div className="text-center mb-6">
                  <div className="w-48 h-60 rounded-2xl overflow-hidden shadow-lg mx-auto mb-4 hover:scale-105 transition-transform duration-300">
                    <img 
                      src="https://static.readdy.ai/image/600af45053aeb2d03fd9159422e2737a/33334d865d9c8d65ce043d9bdfe0bd68.jpeg"
                      alt="奈良幸子講師"
                      title="奈良幸子 バイオリン・ビオラ講師 国分寺市"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <h4 className="text-2xl font-bold text-gray-800 mb-2">奈良 幸子</h4>
                  <p className="text-lg text-amber-600 mb-4 gradient-text">バイオリン・ビオラ指導</p>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed text-sm">
                  桐朋学園大学卒業。国内主要オーケストラや室内楽で演奏活動を行い、コンサートやテレビ出演、レコーディングなど、様々なジャンルで活動。
                </p>
                <p className="text-gray-700 mb-6 leading-relaxed text-sm">
                  ビオラ奏者としても活躍しています。3児の母として、お子さんから大人まで丁寧に指導いたします。温かい雰囲気の中で音楽の楽しさをお伝えします。
                </p>
                <div className="space-y-2">
                  <div className="flex items-center text-gray-700 text-sm hover:translate-x-1 transition-transform duration-300">
                    <i className="ri-award-line text-amber-600 mr-2 text-lg"></i>
                    <span>桐朋学園大学卒業</span>
                  </div>
                  <div className="flex items-center text-gray-700 text-sm hover:translate-x-1 transition-transform duration-300">
                    <i className="ri-music-line text-amber-600 mr-2 text-lg"></i>
                    <span>バイオリン・ビオラ指導</span>
                  </div>
                  <div className="flex items-center text-gray-700 text-sm hover:translate-x-1 transition-transform duration-300">
                    <i className="ri-team-line text-amber-600 mr-2 text-lg"></i>
                    <span>弦楽アンサンブルの指導・演奏</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Access Section */}
      <section id="access" className="py-20 px-4 bg-white scroll-animate">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h4 className="text-4xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Noto Serif JP', serif" }}>アクセス</h4>
            <div className="w-20 h-1 bg-amber-600 mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-1 gap-12">
            <div>
              <div className="bg-amber-50 rounded-2xl p-8 mb-8 hover-scale scroll-animate">
                <h4 className="text-2xl font-bold text-gray-800 mb-6">教室所在地</h4>
                <div className="space-y-4">
                  <div className="flex items-start hover:translate-x-1 transition-transform duration-300">
                    <i className="ri-map-pin-line text-amber-600 text-2xl mr-4 mt-1 animate-pulse-slow"></i>
                    <div>
                      <div className="font-semibold text-gray-800 mb-1">住所</div>
                      <div className="text-gray-700">東京都国分寺市(詳細はお問い合わせください) 駐車場あり</div>
                    </div>
                  </div>
                  <div className="flex items-start hover:translate-x-1 transition-transform duration-300">
                    <i className="ri-train-line text-amber-600 text-2xl mr-4 mt-1 animate-pulse-slow"></i>
                    <div>
                      <div className="font-semibold text-gray-800 mb-1">最寄り駅</div>
                      <div className="text-gray-700">JR中央線・武蔵野線「西国分寺駅」徒歩10分</div>
                    </div>
                  </div>
                  <div className="flex items-start hover:translate-x-1 transition-transform duration-300">
                    <i className="ri-time-line text-amber-600 text-2xl mr-4 mt-1 animate-pulse-slow"></i>
                    <div>
                      <div className="font-semibold text-gray-800 mb-1">レッスン時間</div>
                      <div className="text-gray-700">
                        9:30〜20:30<br />
                        土日祝も対応
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start hover:translate-x-1 transition-transform duration-300">
                    <i className="ri-phone-line text-amber-600 text-2xl mr-4 mt-1 animate-pulse-slow"></i>
                    <div>
                      <div className="font-semibold text-gray-800 mb-1">電話番号</div>
                      <div className="text-gray-700">042-502-0408</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-amber-600 to-amber-700 rounded-2xl p-8 text-white hover-scale scroll-animate">
                <h4 className="text-2xl font-bold mb-4">無料体験レッスン受付中</h4>
                <p className="mb-6">実際の雰囲気を体験していただけます。お気軽にお申し込みください。</p>
                <a href="#contact" className="inline-block bg-white text-amber-600 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300 hover:scale-105 hover:shadow-lg whitespace-nowrap cursor-pointer">
                  体験レッスンを申し込む
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 scroll-animate">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h4 className="text-4xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Noto Serif JP', serif" }}>お問い合わせ</h4>
            <div className="w-20 h-1 bg-amber-600 mx-auto"></div>
            <p className="text-gray-700 text-lg">
              レッスンに関するご質問や体験レッスンのお申し込みは、<br />
              下記フォームよりお気軽にお問い合わせください。
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 hover-scale scroll-animate">
            <form id="contact-form" data-readdy-form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-gray-700 font-semibold mb-2">お名前 <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent outline-none transition-all duration-300 text-sm hover:border-amber-300"
                    placeholder="山田 太郎"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2">電話番号 <span className="text-red-500">*</span></label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent outline-none transition-all duration-300 text-sm hover:border-amber-300"
                    placeholder="090-1234-5678"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="email" className="block text-gray-700 font-semibold mb-2">メールアドレス <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent outline-none transition-all duration-300 text-sm hover:border-amber-300"
                  placeholder="example@email.com"
                />
              </div>
              <div>
                <label htmlFor="age" className="block text-gray-700 font-semibold mb-2">年齢</label>
                <input
                  type="text"
                  id="age"
                  name="age"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent outline-none transition-all duration-300 text-sm hover:border-amber-300"
                  placeholder="例：7歳、25歳"
                />
              </div>
              <div>
                <label htmlFor="experience" className="block text-gray-700 font-semibold mb-2">バイオリン経験 <span className="text-red-500">*</span></label>
                <div className="relative">
                  <select
                    id="experience"
                    name="experience"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent outline-none transition-all duration-300 appearance-none pr-8 text-sm cursor-pointer hover:border-amber-300"
                  >
                    <option value="">選択してください</option>
                    <option value="未経験">未経験</option>
                    <option value="1年未満">1年未満</option>
                    <option value="1-3年">1-3年</option>
                    <option value="3-5年">3-5年</option>
                    <option value="5年以上">5年以上</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                </div>
              </div>
              <div>
                <label htmlFor="course" className="block text-gray-700 font-semibold mb-2">ご希望のコース</label>
                <div className="relative">
                  <select
                    id="course"
                    name="course"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent outline-none transition-all duration-300 appearance-none pr-8 text-sm cursor-pointer hover:border-amber-300"
                  >
                    <option value="">選択してください</option>
                    <option value="子供コース">子供コース</option>
                    <option value="大人コース">大人コース</option>
                    <option value="音大受験コース">音大受験コース</option>
                    <option value="体験レッスン">体験レッスン</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                </div>
              </div>
              <div>
                <label htmlFor="message" className="block text-gray-700 font-semibold mb-2">お問い合わせ内容 <span className="text-red-500">*</span></label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent outline-none transition-all duration-300 resize-none text-sm hover:border-amber-300"
                  placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  <span id="char-count">{charCount}</span> / 500文字
                </div>
              </div>
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-amber-600 text-white px-12 py-4 rounded-full hover:bg-amber-700 transition-all duration-300 hover:scale-105 hover:shadow-lg text-lg font-semibold shadow-lg whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed animate-pulse-slow"
                >
                  {isSubmitting ? '送信中...' : '送信する'}
                </button>
              </div>
              <div id="form-message" className="text-center mt-4"></div>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12 px-4 scroll-animate">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div className="scroll-animate">
              <div className="flex items-center space-x-3 mb-4">
                <div className="relative w-10 h-10 flex items-center justify-center animate-float">
                  <svg width="40" height="40" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="24" cy="24" r="22" fill="url(#gradient2)" stroke="#D97706" strokeWidth="1.5"/>
                    <path d="M18 14L30 34M28 16L32 24M16 24L20 32" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="29" cy="17" r="2" fill="#FFFFFF"/>
                    <circle cx="19" cy="31" r="2" fill="#FFFFFF"/>
                    <path d="M26 20C26 20 28 22 28 24C28 26 26 28 26 28" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round"/>
                    <defs>
                      <linearGradient id="gradient2" x1="4" y1="4" x2="44" y2="44" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#F59E0B"/>
                        <stop offset="1" stopColor="#D97706"/>
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <h4 className="text-xl font-bold" style={{ fontFamily: "'Noto Serif JP', serif" }}>矢谷和子バイオリン教室</h4>
              </div>
              <p className="text-gray-400 leading-relaxed">
                東京都国分寺市で65年の伝統を誇るバイオリン教室。アットホームな雰囲気で丁寧に指導いたします。
              </p>
            </div>
            <div className="scroll-animate">
              <h4 className="text-lg font-bold mb-4">クイックリンク</h4>
              <ul className="space-y-2">
                <li><a href="#about" className="text-gray-400 hover:text-amber-600 transition-all duration-300 hover:translate-x-1 cursor-pointer">教室について</a></li>
                <li><a href="#courses" className="text-gray-400 hover:text-amber-600 transition-all duration-300 hover:translate-x-1 cursor-pointer">レッスン内容</a></li>
                <li><a href="#instructor" className="text-gray-400 hover:text-amber-600 transition-all duration-300 hover:translate-x-1 cursor-pointer">講師紹介</a></li>
                <li><a href="#access" className="text-gray-400 hover:text-amber-600 transition-all duration-300 hover:translate-x-1 cursor-pointer">アクセス</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-amber-600 transition-all duration-300 hover:translate-x-1 cursor-pointer">お問い合わせ</a></li>
              </ul>
            </div>
            <div className="scroll-animate">
              <h4 className="text-lg font-bold mb-4">レッスン時間</h4>
              <ul className="space-y-2 text-gray-400">
                <li>9:30〜20:30</li>
                <li>土日祝も対応</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center text-gray-400">
            <p>&copy; 2024 矢谷和子バイオリン教室. All rights reserved. | <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-amber-600 transition-all duration-300 hover:scale-105 cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
